import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, useEffect } from 'react';
import { Banknote, CheckCircle2, DollarSign } from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';

export function CommissionsPage() {
  const [plans, setPlans] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/accounting/commissions/plans').then((r) => r.json()),
      fetch('/api/accounting/commissions/agents').then((r) => r.json()),
    ]).then(([plansData, agentsData]) => {
      if (plansData.ok || plansData.success) setPlans(Array.isArray(plansData.data) ? plansData.data : []);
      if (agentsData.ok || agentsData.success) setAgents(Array.isArray(agentsData.data) ? agentsData.data : []);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="text-slate-900 dark:text-slate-100 space-y-6 pb-16">
      <PageHeader
        icon={Banknote}
        eyebrow="Finance"
        title="Sales Commissions & Agent Statements"
        description={<>Manage commission plans, paid-invoice accruals, payouts, and sales return clawbacks.</>}
      />

      {loading ? (
        <div className="p-12 text-center text-slate-500">Loading commission plans & agents...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Plans */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <h2 className="text-sm font-bold flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-500" /> Active Commission Plans
            </h2>
            <div className="space-y-3">
              {plans.map((p) => (
                <div key={p._id} className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-sm text-slate-900 dark:text-slate-100">{p.name}</div>
                    <div className="text-xs text-slate-500 capitalize">
                      Type: {p.planType} · Rate: {p.ratePct}%
                    </div>
                  </div>
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                    Active
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Agents */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <h2 className="text-sm font-bold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-indigo-500" /> Commission Agent Statements
            </h2>
            <div className="space-y-3">
              {agents.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-400">No agents registered yet.</div>
              ) : (
                agents.map((a) => (
                  <div key={a._id} className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs">
                    <div>
                      <div className="font-bold text-sm text-slate-900 dark:text-slate-100">{a.agentName}</div>
                      <div className="text-slate-500">
                        Earned: {formatINR(a.earnedMinor / 100)} · Paid: {formatINR(a.paidMinor / 100)}
                      </div>
                    </div>
                    <div className="text-right font-mono">
                      <div className="text-[10px] text-slate-500 uppercase font-bold">Balance Payable</div>
                      <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                        {formatINR(a.balancePayableMinor / 100)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommissionsPage;
